import React, { PropsWithChildren, useContext, useEffect } from "react";
import {
  Input,
  MenuItem,
  Select,
  Typography,
  FormHelperText,
  Box,
} from "@material-ui/core";
import { useForm } from "react-hook-form";
import moment from "moment";
import useStyles from "./styles";
import { translate as t } from "../../../../../infrastructure/translate/localization";
import TaxRegimesContext from "../../../../../infrastructure/Contexts/components/data/TaxRegimesWrapper/context";
import BenefitPlansContext from "../../../../../infrastructure/Contexts/components/data/BenefitPlanWrapper/context";
import CobeeButton from "../../../CobeeButton";
import validateFieldByType, {
  fieldTypesConfig,
} from "../../../../../infrastructure/validateFieldByType";
import EmployeeActionsContext from "../../../../../infrastructure/Contexts/components/actions/EmployeeActionsWrapper/context";
import Employee from "../../../../domain/Employee";

const columnBorderStyle = "0.5px solid #cfdae5";

/* eslint-disable react/require-default-props */
const FieldTitle = ({
  children,
  required,
}: PropsWithChildren<{ required?: boolean }>) => (
  <Typography variant="caption" color="textSecondary">
    {children}{" "}
    {required && (
      <Typography component="span" style={{ color: "#E633C8" }}>
        *
      </Typography>
    )}
  </Typography>
);
export default function EmployeeDetailsModal({
  defaultValues = undefined,
}: {
  defaultValues?: any;
}) {
  const { input, inputErrorMessage } = useStyles();

  const { taxRegimes } = useContext(TaxRegimesContext);
  const { benefitPlans } = useContext(BenefitPlansContext);
  const {
    createNewEmployee,
    editEmployeeDetails,
    createNewEmployeeIsLoading,
    editEmployeeDetailsIsLoading,
  } = useContext(EmployeeActionsContext);

  const { handleSubmit, register, errors, setValue, getValues } = useForm();

  useEffect(() => {
    if (defaultValues) {
      const formValues = getValues();
      const formKeys = Object.keys(formValues);
      formKeys.forEach((key) => {
        if (key in defaultValues) {
          if (key === "hiringDate") {
            setValue(key, moment(defaultValues[key]).format("YYYY-MM-DD"), {
              shouldValidate: true,
              shouldDirty: true,
            });
          } else {
            setValue(key, defaultValues[key], {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
        }
      });
    }
  }, [defaultValues, getValues, setValue]);

  const onSubmit = ({ benefitPlan, taxRegime, ...rest }) => {
    if (typeof defaultValues === "object") {
      editEmployeeDetails({
        employees: [new Employee({ ...defaultValues, ...rest })],
        benefitPlanId: benefitPlan,
        taxRegime,
      });
    } else {
      createNewEmployee({
        employee: new Employee({ ...rest }),
        benefitPlanId: benefitPlan,
        taxRegime,
      });
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      display="flex"
      flexDirection="column"
    >
      <Box display="flex" mb={6}>
        <Box
          width={280}
          pr={3}
          sx={{
            borderRight: columnBorderStyle,
          }}
        >
          <Box mb={4}>
            <FieldTitle required>{t("common-messages.name")}</FieldTitle>
            <Input
              className={input}
              type="text"
              name="name"
              inputRef={register({
                required: t("common-messages.field-is-required"),
              })}
              aria-describedby="name-error-text"
            />
            {"name" in errors && (
              <FormHelperText
                id="name-error-text"
                className={inputErrorMessage}
              >
                {errors.name?.message}
              </FormHelperText>
            )}
          </Box>
          <Box mb={4}>
            <FieldTitle required>{t("common-messages.surnames")}</FieldTitle>
            <Input
              className={input}
              type="text"
              name="surname"
              inputRef={register({
                required: t("common-messages.field-is-required"),
              })}
              aria-describedby="first-surname-error-text"
            />
            {"surname" in errors && (
              <FormHelperText
                id="first-surname-error-text"
                className={inputErrorMessage}
              >
                {errors.surname?.message}
              </FormHelperText>
            )}
          </Box>

          <Box mb={4}>
            <FieldTitle required>{t("common-messages.legal-id")}</FieldTitle>
            <Input
              className={input}
              type="text"
              name="legalId"
              inputRef={register({
                required: t("common-messages.field-is-required"),
                validate: validateFieldByType({ type: "legalId" }),
              })}
              aria-describedby="legal-id-error-text"
            />
            {"legalId" in errors && (
              <FormHelperText
                id="legal-id-error-text"
                className={inputErrorMessage}
              >
                {errors.legalId.type === "validate"
                  ? fieldTypesConfig.legalId.message
                  : errors.legalId?.message}
              </FormHelperText>
            )}
          </Box>
          <Box mb={4}>
            <FieldTitle required>{t("common-messages.email")}</FieldTitle>
            <Input
              className={input}
              type="email"
              name="email"
              inputRef={register({
                required: t("common-messages.field-is-required"),
              })}
              aria-describedby="email-error-text"
            />
            {"email" in errors && (
              <FormHelperText
                id="second-surname-error-text"
                className={inputErrorMessage}
              >
                {errors["second-surname"]?.message}
              </FormHelperText>
            )}
          </Box>
          <Box mb={4}>
            <FieldTitle>{t("common-messages.internal-id")}</FieldTitle>
            <Input
              className={input}
              type="text"
              name="internalId"
              inputRef={register}
            />
          </Box>
        </Box>
        <Box
          width={280}
          pl={3}
          sx={{
            borderLeft: columnBorderStyle,
          }}
        >
          <Box mb={4}>
            <FieldTitle required>
              {t("upload-employees-section.hiring-date")}
            </FieldTitle>
            <Input
              className={input}
              type="date"
              name="hiringDate"
              inputRef={register({
                required: t("common-messages.field-is-required"),
              })}
            />
            {"hiringDate" in errors && (
              <FormHelperText id="hiringDate" className={inputErrorMessage}>
                {errors.hiringDate?.message}
              </FormHelperText>
            )}
          </Box>
          <Box mb={4}>
            <FieldTitle required={!defaultValues}>
              {t("upload-employees-section.annual-gross-salary")}
            </FieldTitle>
            <Input
              className={input}
              type="number"
              name="grossSalary"
              placeholder={
                typeof defaultValues === "object"
                  ? t("upload-employees-section.hidden")
                  : ""
              }
              inputRef={register({
                required: defaultValues
                  ? false
                  : t("common-messages.field-is-required"),
              })}
              inputProps={{ min: 0, step: "1" }}
              aria-describedby="annual-gross-salary-error-text"
            />
            {"grossSalary" in errors && (
              <FormHelperText
                id="annual-gross-salary"
                className={inputErrorMessage}
              >
                {errors.grossSalary?.message}
              </FormHelperText>
            )}
          </Box>
          <Box mb={4}>
            <FieldTitle>{t("employees-section.tax-regime")}</FieldTitle>
            <Select
              defaultValue={defaultValues?.taxRegime || taxRegimes[0]}
              className={input}
              inputProps={{
                inputRef: (ref) => {
                  if (!ref) return;
                  register({
                    name: "taxRegime",
                    value: ref.value,
                  });
                },
              }}
              onChange={(e) => setValue("taxRegime", e.target.value)}
            >
              {taxRegimes.map((taxRegime) => (
                <MenuItem key={taxRegime} value={taxRegime}>
                  {t(`employees-section.regime-${taxRegime}`)}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box mb={4}>
            <FieldTitle>{t("common-messages.cost-center")}</FieldTitle>
            <Input
              className={input}
              type="text"
              name="costCenter"
              inputRef={register}
            />
          </Box>
          <Box mb={4}>
            <FieldTitle>
              {t("common-messages.company-name-employer")}
            </FieldTitle>
            <Input
              className={input}
              type="text"
              name="payrollCompany"
              inputRef={register}
            />
          </Box>
          <Box mb={4}>
            <FieldTitle>{t("benefits-section.benefit-plan")}</FieldTitle>
            <Select
              defaultValue={defaultValues?.benefitPlan || benefitPlans[0]?.id}
              className={input}
              inputProps={{
                inputRef: (ref) => {
                  if (!ref) return;
                  register({
                    name: "benefitPlan",
                    value: ref.value,
                  });
                },
              }}
              onChange={(e) => setValue("benefitPlan", e.target.value)}
            >
              {benefitPlans.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography
          component="span"
          variant="body2"
          style={{ color: "#E633C8" }}
        >
          {t("common-messages.required-fields")}*
        </Typography>
        <CobeeButton
          type="submit"
          styleType="primary"
          isLoading={createNewEmployeeIsLoading || editEmployeeDetailsIsLoading}
        >
          <Typography variant="body2">{t("actions.confirm")}</Typography>
        </CobeeButton>
      </Box>
    </Box>
  );
}
